import { makeAutoObservable } from "mobx";
import { ICompareValueStore } from "./compare-value-factory";
import WeightCompareValueField from "../../components/policy-editor/policy-eligibility-card/condition-group-card/condition-builder/condition-field/compare-value-type-fields/weight-compare-value-field";
import { CONDITION_OPERATORS, ICompareValueItem, IConditionFieldPreset } from "../../policy-types";
import { IValueTypeCompareValue } from "./window-compare-value-store";
import { toNumber } from "../../components/policy-editor/policy-eligibility-card/condition-group-card/condition-builder/helpers/type-convertions";

class WeightCompareValueStore implements ICompareValueStore<IValueTypeCompareValue> {
    private _model: IValueTypeCompareValue = {} as IValueTypeCompareValue;
    private _changes: Partial<IValueTypeCompareValue> = {};
    private _uncommittedChanges: Partial<IValueTypeCompareValue> = {};

    constructor(private _fieldPreset: IConditionFieldPreset, compareValue?: string) {
        makeAutoObservable(this);
        if (compareValue) {
            this._model = this._deserializeModel(compareValue);
        }
    }

    public setChanges(changes?: string) {
        if (changes) {
            this._changes = this._deserializeModel(changes);
        }
    }

    private _deserializeModel(compareValue: string): IValueTypeCompareValue {
        let model = {} as IValueTypeCompareValue;
        try {
            model = JSON.parse(compareValue);
        }
        catch(e) {
            /** should never happen */
            console.error(`invalid condition's compare value from DB: ${compareValue}`);
        }
        return model;
    }

    get options() {
        return this._fieldPreset.compare_value_data.map(v => {
            return {
                value: (v as ICompareValueItem).value ?? '',
                label: (v as ICompareValueItem).name
            }
        });
    }

    get Component() {
        return WeightCompareValueField;
    }

    public get value() {
        return this._uncommittedChanges.value ?? this._changes.value ?? this._model.value;
    }

    public get type() {
        return this._uncommittedChanges.type ?? this._changes.type ?? this._model.type;
    }

    public onOperatorChanged(op: CONDITION_OPERATORS): void {
    }

    public setValue(v: number) {
        this._uncommittedChanges.value = toNumber(v);
    }

    public setType(v: string) {
        this._uncommittedChanges.type = v;
    }

    public commitChanges(): void {
        this._changes = {...this._changes, ...this._uncommittedChanges};
        this._clearUnneededChanges();
    }

    public discardUncommittedChanges(): void {
        this._uncommittedChanges = {};
    }

    public hasChanges(): boolean {
        return Object.values(this._changes).length > 0;
    }

    public discardChanges(): void {
        this._changes = {};
    }

    public getUpdatedModel(): string|undefined {
        /** if empty serialized - return undefined */
        const serialized = JSON.stringify({ ...this._model, ...this._changes });
        if (serialized === JSON.stringify({})) {
            return undefined;
        }
        return serialized;
    }

    public getUncommittedChanges() {
        return { ...this._model, ...this._changes, ...this._uncommittedChanges };
    }

    private _clearUnneededChanges() {
        Object.keys(this._changes).forEach(key => {
            const theKey = key as keyof IValueTypeCompareValue;
            if (this._changes[theKey] === this._model?.[theKey]) {
                delete this._changes[theKey];
            }
        });
    }
}

export default WeightCompareValueStore;
